const ICON_MUTED =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M5 9v6h4l5 5V4L9 9H5zm11.1 3L19 9.1 17.6 7.7 14.7 10.6l-2.1-2.1-1.4 1.4 2.1 2.1-2.1 2.1 1.4 1.4 2.1-2.1 2.9 2.9 1.4-1.4z"/></svg>';
const ICON_ON =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 8.1v7.8A4.5 4.5 0 0 0 16.5 12zM14 3.2v2.1A7.5 7.5 0 0 1 21 12a7.5 7.5 0 0 1-7 6.7v2.1A9.5 9.5 0 0 0 23 12 9.5 9.5 0 0 0 14 3.2z"/></svg>';
const ICON_EXPAND =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm0-4h2V7h3V5H5v5h2zm10 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>';
const ICON_CLOSE =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7 4.3 4.3l6.3 6.3 6.3-6.3z"/></svg>';

function setSound(video, btn, on) {
  video.muted = !on;
  btn.setAttribute("aria-pressed", on ? "true" : "false");
  btn.setAttribute("aria-label", on ? "Mute" : "Unmute");
  btn.innerHTML = on ? ICON_ON : ICON_MUTED;
}

function muteSiblings(root, keep) {
  root.querySelectorAll("video").forEach((other) => {
    if (other === keep) return;
    const otherBtn = other.parentElement?.querySelector(
      ".escape-pdp-reel__audio, .escape-pdp-reel-viewer__audio",
    );
    if (otherBtn) setSound(other, otherBtn, false);
  });
}

class EscapePdpReel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === "true") return;
    this.dataset.loaded = "true";
    void this.load();
  }

  async load() {
    const productId = (this.dataset.productId || "").trim();
    const opsUrl = (this.dataset.opsUrl || "").replace(/\/+$/, "");
    if (!productId || !opsUrl) return;

    try {
      const url = `${opsUrl}/api/public/reels?productId=${encodeURIComponent(productId)}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();
      const reels = Array.isArray(data?.reels)
        ? data.reels.filter((r) => r?.url)
        : data?.reel?.url
          ? [data.reel]
          : [];
      if (!reels.length) return;
      this.mount(reels);
    } catch {
      // fail closed on the storefront; editor keeps the placeholder
    }
  }

  mount(reels) {
    const frame = this.querySelector("[data-reel-frame]");
    if (!frame) return;

    const autoplay = this.dataset.autoplay !== "false";
    const loop = this.dataset.loop !== "false";
    this.reels = reels;
    this.loop = loop;
    this.querySelector("[data-reel-placeholder]")?.remove();
    frame.replaceChildren();

    const scroller = document.createElement("div");
    scroller.className = "escape-pdp-reel__scroller";

    reels.forEach((reel, index) => {
      const slide = document.createElement("div");
      slide.className = "escape-pdp-reel__slide";
      const video = document.createElement("video");
      video.className = "escape-pdp-reel__video";
      video.src = reel.url;
      if (reel.posterUrl) video.poster = reel.posterUrl;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute("muted", "");
      video.setAttribute("playsinline", "");
      video.preload = "metadata";
      video.loop = loop;

      const audioBtn = document.createElement("button");
      audioBtn.type = "button";
      audioBtn.className = "escape-pdp-reel__audio";
      setSound(video, audioBtn, false);
      audioBtn.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const turnOn = video.muted;
        muteSiblings(scroller, video);
        setSound(video, audioBtn, turnOn);
        video.play().catch(() => {});
      });

      const openBtn = document.createElement("button");
      openBtn.type = "button";
      openBtn.className = "escape-pdp-reel__open";
      openBtn.setAttribute("aria-label", "Open fullscreen");
      openBtn.innerHTML = ICON_EXPAND;
      openBtn.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openViewer(index, !video.muted);
      });

      slide.addEventListener("click", () => {
        this.openViewer(index, !video.muted);
      });

      slide.append(video, openBtn, audioBtn);
      scroller.append(slide);
    });

    frame.append(scroller);
    this.hidden = false;
    this.classList.add("is-ready");

    if (!autoplay) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      },
      { root: scroller, threshold: [0, 0.5, 1] },
    );

    scroller.querySelectorAll("video").forEach((video) => io.observe(video));
  }

  openViewer(startIndex, soundOn) {
    this.closeViewer();

    const dialog = document.createElement("dialog");
    dialog.className = "escape-pdp-reel-viewer";
    dialog.setAttribute("aria-label", "Product reels");

    const scroller = document.createElement("div");
    scroller.className = "escape-pdp-reel-viewer__scroller";

    this.reels.forEach((reel) => {
      const slide = document.createElement("div");
      slide.className = "escape-pdp-reel-viewer__slide";
      const video = document.createElement("video");
      video.className = "escape-pdp-reel-viewer__video";
      video.src = reel.url;
      if (reel.posterUrl) video.poster = reel.posterUrl;
      video.playsInline = true;
      video.setAttribute("playsinline", "");
      video.preload = "metadata";
      video.loop = this.loop !== false;

      const audioBtn = document.createElement("button");
      audioBtn.type = "button";
      audioBtn.className = "escape-pdp-reel-viewer__audio";
      setSound(video, audioBtn, false);
      audioBtn.addEventListener("click", (event) => {
        event.stopPropagation();
        const turnOn = video.muted;
        muteSiblings(scroller, video);
        setSound(video, audioBtn, turnOn);
        video.play().catch(() => {});
      });

      slide.append(video, audioBtn);
      slide.addEventListener("click", (event) => {
        if (event.target.closest("button")) return;
        if (video.paused) video.play().catch(() => {});
        else video.pause();
      });
      scroller.append(slide);
    });

    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "escape-pdp-reel-viewer__close";
    closeBtn.setAttribute("aria-label", "Close");
    closeBtn.innerHTML = ICON_CLOSE;
    closeBtn.addEventListener("click", () => this.closeViewer());

    dialog.append(scroller, closeBtn);
    document.body.append(dialog);

    const sync = () => {
      const h = scroller.clientHeight || 1;
      const active = Math.min(
        this.reels.length - 1,
        Math.max(0, Math.round(scroller.scrollTop / h)),
      );
      scroller.querySelectorAll("video").forEach((video, i) => {
        if (i === active) video.play().catch(() => {});
        else video.pause();
      });
    };

    scroller.addEventListener("scroll", sync, { passive: true });
    dialog.addEventListener("close", () => this.closeViewer());
    dialog.addEventListener("cancel", () => this.closeViewer());

    this.viewer = { dialog, scroller };
    this.querySelectorAll(".escape-pdp-reel__video").forEach((video) =>
      video.pause(),
    );
    document.documentElement.classList.add("escape-pdp-reel-lock");
    dialog.showModal();

    const start = Math.min(startIndex, this.reels.length - 1);
    scroller.scrollTop = scroller.clientHeight * start;

    const startVideo = scroller.querySelectorAll("video")[start];
    const startBtn = startVideo?.parentElement?.querySelector(
      ".escape-pdp-reel-viewer__audio",
    );
    if (startVideo && startBtn) {
      muteSiblings(scroller, startVideo);
      setSound(startVideo, startBtn, soundOn);
      startVideo.play().catch(() => {});
    }

    requestAnimationFrame(sync);
  }

  closeViewer() {
    const viewer = this.viewer;
    this.viewer = null;
    document.documentElement.classList.remove("escape-pdp-reel-lock");
    if (!viewer) return;
    viewer.scroller.querySelectorAll("video").forEach((video) => {
      video.pause();
      video.src = "";
    });
    viewer.dialog.close();
    viewer.dialog.remove();
  }
}

if (!customElements.get("escape-pdp-reel")) {
  customElements.define("escape-pdp-reel", EscapePdpReel);
}
